import ApiService from '@/services/apiService';
import moment from 'moment';
import { nanoid } from 'nanoid';
import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { pullAt, get, isEqual, sortBy, isEmpty } from 'lodash';
import { codemirror } from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/base16-dark.css';
import 'codemirror/theme/duotone-light.css';
import 'codemirror/mode/javascript/javascript.js';
export default {
  name: 'CoreDtwinRuleTable',
  components: {
    codemirror
  },
  props: {
    dtwin: {
      type: Object,
      required: false,
      default: () => ({})
    },
    // the states that are to be displayed / edited
    value: {
      type: Array,
      required: true
    },
    stateTable: {
      type: Object,
      required: false,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    hasTwinEditPermission: {
      type: Boolean,
      required: false,
      default: false
    },
    hasTwinManagePermission: {
      type: Boolean,
      required: false,
      default: false
    },
    isOpen: {
      type: Boolean,
      default: true
    },
    template: {
      type: Object,
      required: false,
      default: () => ({})
    },
    isCreatedByTemplate: {
      type: Boolean,
      required: false,
      default: false
    },
    isProductiveVersion: {
      type: Boolean,
      optional: true,
      default: false
    },
    readonly: {
      type: Boolean,
      optional: true,
      default: false
    }
  },
  data: function () {
    return {
      historyQueryStartDate: new Date(new Date() - 86400000 * 5),
      historyQueryEndDate: new Date(),
      ruleEditDialog: false,
      ruleEditServerRequest: false,
      deleteDialog: false,
      targetRuleIndex: -1,
      historyDialog: false,
      copyRuleDialog: false,
      executionHistoryCmdFilter: false,
      executionHistoryRule: {},
      executionHistoryTableOptions: {},
      executionHistory: [],
      executionHistoryItemsLength: -1,
      executionHistoryTableHeaders: [{
        text: this.$i18n_t('Common.date'),
        value: 'date',
        sortable: false
      }, {
        text: this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.executionConditionResult'),
        value: 'conditionsResult',
        sortable: false
      }, {
        text: this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.executionConditionChanged'),
        value: 'changed',
        sortable: false
      }, {
        text: this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.commands'),
        value: 'commands',
        sortable: false
      }, {
        text: '',
        value: 'data-table-expand'
      }],
      executionHistoryTableFooter: {
        itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
        itemsPerPageOptions: [15, 30, 50, 100]
      },
      executionHistoryExportDialog: false,
      expandedExecution: [],
      cmOptions: {
        tabSize: 4,
        mode: 'application/ld+json',
        theme: this.$store.state.app.UiTheme ? 'base16-dark' : 'duotone-light',
        lineNumbers: false,
        line: true,
        foldGutter: true,
        styleActiveLine: true,
        styleSelectedText: true,
        readOnly: true,
        hintOptions: {
          completeSingle: false
        },
        lineWrapping: false,
        viewportMargin: Infinity
      },
      editRuleFormIsValid: false,
      bridgeTypes: [],
      bridgeInstances: [],
      rules: []
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    detailButtonIcon() {
      return this.hasTwinEditPermission ? 'fa-edit' : 'fa-eye';
    },
    emptyIndexObject() {
      // if a twin(state) update comes in, the `value` watcher will fetch all rules again
      // triggering a re-computation of the `core-dtwin-rule-edit`-value field,
      // which defaults to an "empty" object for a new rule
      // to make sure while an edit-dialog is opened no new object is created, put this into a computed prop
      const targetRuleIndex = this.targetRuleIndex;
      return {
        targetRuleIndex
      };
    },
    isTemplateContext() {
      return !isEmpty(this.template);
    },
    buttons() {
      return [{
        disabled: this.isProductiveVersion || this.readonly,
        icon: 'fal fa-plus',
        action: 'addItem',
        dataCy: 'add_rule',
        valid: !this.disabled && this.hasTwinEditPermission,
        tooltip: this.$i18n_t('Common.addNew')
      }];
    }
  },
  watch: {
    ruleEditDialog() {
      if (!this.ruleEditDialog) {
        this.targetRuleIndex = this.targetRuleIndex >= 0 ? -1 : this.targetRuleIndex - 1;
      }
    },
    executionHistoryTableOptions: {
      handler() {
        if (this.executionHistoryRule) {
          this.fetchRuleExecutionHistory(this.executionHistoryRule, {
            resetTableOptions: false,
            resetDates: false
          });
        }
      },
      deep: true
    },
    executionHistoryCmdFilter() {
      this.fetchRuleExecutionHistory(this.executionHistoryRule, {
        resetTableOptions: false,
        resetDates: false
      });
    },
    dtwin() {
      this.fetchTwinRules();
    },
    template() {
      this.rules = this.template.ruleDefinition;
    }
  },
  mounted() {
    this.fetchTwinRules();
    if (this.isCreatedByTemplate) {
      const indexOfAddBtn = this.buttons.findIndex(btn => btn.action === 'addItem');
      this.buttons[indexOfAddBtn].disabled = true;
    }
  },
  methods: {
    openState(stateId) {
      const state = this.value.find(s => s.id === stateId);
      this.stateTable.editState(state);
    },
    fetchTwinRules() {
      let bridgeTypesPromise = this.hasTwinEditPermission ? ApiService.getBridgeTypes().then(bridgeTypes => {
        return bridgeTypes.filter(type => {
          // only display bridges that have instance commands
          return Object.keys(get(type, 'manifest.commands.instance', {})).length > 0;
        });
      }) : Promise.resolve([]);
      let tenantBridgepromise = this.hasTwinEditPermission ? ApiService.getBridgesForTenant(this.isTemplateContext ? this.template.tenantId : this.dtwin.tenantId) : Promise.resolve([]);
      const promises = [bridgeTypesPromise, tenantBridgepromise];
      if (!this.isTemplateContext || this.isCreatedByTemplate) {
        promises.push(ApiService.digitalTwinFetchRules(this, this.dtwin.id));
      }
      return Promise.all(promises).then(results => {
        this.bridgeTypes = results[0];
        this.bridgeInstances = results[1];
        if (results[2]) {
          this.rules = results[2].sort((a, b) => {
            return a.id - b.id;
          });
        } else {
          this.rules = this.template.ruleDefinition;
        }
        this.sanitizeExecutionTimes(this.rules);
        if (get(this.$route, 'query.ruleId')) {
          const ruleIndex = this.rules.findIndex(r => r.id === parseInt(get(this.$route, 'query.ruleId')));
          if (ruleIndex >= 0) {
            this.targetRuleIndex = ruleIndex;
            this.ruleEditDialog = true;
          }
        }
      });
    },
    gatherRuleCommands(rule) {
      return rule.properties.actions.map(action => {
        const commandParts = (get(action, 'command') || '').split('.');
        if (commandParts.length === 1) {
          return undefined;
        }
        const bridgeInstanceId = commandParts[0];
        const command = action.command.substring(bridgeInstanceId.length + 1);
        const bridgeInstance = this.bridgeInstances.find(b => b.id === parseInt(bridgeInstanceId));
        return `${get(bridgeInstance, 'name') || 'Unknown'}.${command}`;
      }).filter(v => !!v);
    },
    printRuleExecutionCommands(execution) {
      return execution.properties.executedCommands.map(commandExec => {
        const bridgeInstanceId = commandExec.bridgeId;
        const command = commandExec.command;
        const bridgeInstance = this.bridgeInstances.find(b => b.id === parseInt(bridgeInstanceId));
        return `${get(bridgeInstance, 'name') || 'Unknown'}.${command}`;
      }).filter(v => !!v).join(',') || '-';
    },
    addItem() {
      this.targetRuleIndex = -1;
      this.ruleEditDialog = true;
    },
    deleteRule(index) {
      if (this.rules[index].id) {
        if (this.isTemplateContext) {
          const removedRule = this.template.ruleDefinition[index];
          this.template.ruleDefinition.splice(index, 1);
          ApiService.deviceTemplateUpdate(this.template).then(() => {
            this.$emit('update-unsaved-changes', this.template);
            this.sanitizeExecutionTimes(this.rules);
            this.$snack(this.$i18n_t(`DigitalTwin.DigitalTwin.ruleModeler.${removedRule.id ? 'updated' : 'created'}`) + (removedRule.properties.title || ''), 'success');
          }).catch(error => {
            if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
              this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
            } else {
              this.$snack(get(error, 'response.data.message') || this.$i18n_t('Error.unknown'), 'error');
            }
          });
        } else {
          // TODO: process result
          ApiService.digitalTwinDeleteRule(this, this.dtwin.id, this.rules[index].id);
          pullAt(this.rules, [index]);
          this.rules = cloneDeep(this.rules);
        }
      }
    },
    persistRule(incomingRule) {
      const rule = this.sanitizeRuleDays(incomingRule);
      if (!this.isTemplateContext) {
        rule.dtwinId = this.dtwin.id;
      }
      let promise = null;
      this.ruleEditServerRequest = true;
      if (this.isTemplateContext) {
        if (rule.id) {
          const ruleIndex = this.template.ruleDefinition.findIndex(r => r.id === incomingRule.id);
          this.template.ruleDefinition[ruleIndex] = rule;
        } else {
          rule.id = nanoid(10);
          rule.properties.templateRuleId = rule.id;
          this.rules.push(rule);
        }
        promise = ApiService.deviceTemplateUpdate(this.template).then(template => {
          if (incomingRule.id) {
            const index = template.ruleDefinition.findIndex(rule => rule.id === incomingRule.id);
            this.rules[index] = template.ruleDefinition[index];
          }
          this.sanitizeExecutionTimes(this.rules);
          if (incomingRule.id) {
            this.rules = cloneDeep(this.rules);
          }
          this.$emit('update-unsaved-changes', this.template);
          this.$snack(this.$i18n_t(`DigitalTwin.DigitalTwin.ruleModeler.${incomingRule.id ? 'updated' : 'created'}`) + (incomingRule.properties.title || ''), 'success');
        });
      } else {
        if (rule.id) {
          promise = ApiService.digitalTwinUpdateRule(this, rule).then(updatedRule => {
            let index = this.rules.findIndex(r => r.id === updatedRule.id);
            this.rules[index] = updatedRule;
            this.sanitizeExecutionTimes(this.rules);
            this.rules = cloneDeep(this.rules);
            this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.updated') + (updatedRule.properties.title || ''), 'success');
          });
        } else {
          promise = ApiService.digitalTwinCreateRule(this, rule).then(createdRule => {
            this.rules.push(createdRule);
            this.sanitizeExecutionTimes(this.rules);
            this.rules = cloneDeep(this.rules);
            this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.created') + (createdRule.properties.title || ''), 'success');
          });
        }
      }
      promise.catch(error => {
        if (this.isTemplateContext && !incomingRule.id) {
          this.rules.pop();
        }
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(get(error, 'response.data.message') || this.$i18n_t('Error.unknown'), 'error');
        }
      }).finally(() => {
        this.ruleEditDialog = false;
        this.ruleEditServerRequest = false;
      });
    },
    printRuleStates(rule) {
      if ((rule.stateIds || []).length === 0) {
        return '-';
      }
      return rule.stateIds.filter(sid => {
        return !!this.value.find(s => s.id === sid);
      }).map(sid => {
        return this.value.find(s => s.id === sid).properties.title;
      }).join(', ');
    },
    localizeDate(date) {
      if (date) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(date).format('L LTS');
      }
      return '-';
    },
    fetchRuleExecutionHistory(rule, options = {
      resetTableOptions: false,
      resetDates: true
    }) {
      const {
        resetTableOptions = false,
        resetDates = true
      } = options;
      if (resetTableOptions) {
        this.executionHistoryRule = rule;
        // reset to page 1,
        this.executionHistoryTableOptions = {
          ...this.executionHistoryTableOptions,
          page: 1,
          itemsPerPage: 15
        };
        // above will trigger a call to fetchRuleExecutionHistory
        return;
      }

      // only update query dates to default when explicitly requested
      if (resetDates) {
        this.historyQueryStartDate = new Date(new Date() - 86400000 * 5);
        this.historyQueryEndDate = new Date();
      }
      const {
        page,
        itemsPerPage
      } = this.executionHistoryTableOptions;
      const usedPage = Math.max(1, page || 1);
      const usedItemsPerPage = itemsPerPage || 15;
      const offset = (usedPage - 1) * usedItemsPerPage;
      this.executionHistoryRule = rule;
      ApiService.digitalTwinFetchRuleExecutionHistory(this, rule.dtwinId, rule.id, this.historyQueryStartDate, this.historyQueryEndDate, [this.executionHistoryCmdFilter ? 'commandsonly' : null].filter(p => p), offset, usedItemsPerPage).then(history => {
        this.executionHistoryItemsLength = get(history, 'totalCount') || 0;
        this.executionHistory = get(history, 'executions') || [];
        this.historyDialog = true;
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(get(error, 'response.data.message', this.$i18n_t('Common.usersFetchFail')), 'error');
        }
      });
    },
    showExecutionHistoryExportDialog(rule) {
      this.executionHistoryRule = rule;
      this.executionHistoryExportDialog = true;
    },
    handleDatePicked(dateObject) {
      this.historyQueryStartDate = new Date(dateObject.from * 1000);
      this.historyQueryEndDate = new Date(dateObject.to * 1000);
      this.fetchRuleExecutionHistory(this.executionHistoryRule, {
        resetTableOptions: true,
        resetDates: false
      });
    },
    sanitizeRuleDays(incomingRule) {
      const rule = cloneDeep(incomingRule);
      rule.properties.executionTimes.forEach(executionTime => {
        if ((executionTime.weekDays || []).includes(this.$i18n_t('Common.AllDays'))) {
          executionTime.weekDays = Array.from({
            length: 7
          }, (idx, i) => i);
        }
        if ((executionTime.monthDays || []).includes(this.$i18n_t('Common.AllDays'))) {
          executionTime.monthDays = Array.from({
            length: 31
          }, (idx, i) => i + 1);
        }
      });
      return rule;
    },
    sanitizeExecutionTimes(rules) {
      rules.forEach(rule => {
        (get(rule, 'properties.executionTimes') || []).forEach(executionTime => {
          if (isEqual(sortBy(executionTime.weekDays, num => num), Array.from({
            length: 7
          }, (idx, i) => i))) {
            executionTime.weekDays = [this.$i18n_t('Common.AllDays')];
          }
          if (isEqual(sortBy(executionTime.monthDays, num => num), Array.from({
            length: 31
          }, (idx, i) => i + 1))) {
            executionTime.monthDays = [this.$i18n_t('Common.AllDays')];
          }
        });
      });
    }
  }
};